import { Toggle } from '@cimpress/react-components';
import { useContext, useEffect, useState } from 'react';
import { getTransferCustomizationUrl } from '../api';
import {
    CardBuilder,
    Control,
    Page,
    PreviewRow,
    PreviewRows,
    PreviewSection,
    TransferCustomizationControls,
} from '../Components';
import { getUrl } from '../tools';
import { useTransferCustomizationParams } from '../Components/TransferCustomization/useTransferCustomizationParams';
import { ApiKeyContext } from '../App';

const intro = `This is the page to test our transfer endpoint.\nFill in the source and target to see the transfer result`;

const SHOW_ALPHA_TOGGLE = false;

export const TransferCustomization = () => {
    const transferCustomizationParams = useTransferCustomizationParams();
    const useAlpha = transferCustomizationParams.useAlpha;
    const setUseAlpha = transferCustomizationParams.setUseAlpha;
    const effectiveUseAlpha = SHOW_ALPHA_TOGGLE && useAlpha;

    const [originRow, setOriginRow] = useState(PreviewRow());
    const [resultRow, setResultRow] = useState(PreviewRow());
    const apiKey = useContext(ApiKeyContext);

    useEffect(() => {
        if (!SHOW_ALPHA_TOGGLE && useAlpha) {
            setUseAlpha(false);
        }
    }, [useAlpha, setUseAlpha]);

    useEffect(() => {
        if (transferCustomizationParams.sourceUrl && transferCustomizationParams.targetUrl) {
            const resultCard = CardBuilder.ofType('documentUrl')
                .withTitle('Result Document')
                .withData({
                    documentUrl:
                        getTransferCustomizationUrl(
                            transferCustomizationParams.sourceUrl,
                            transferCustomizationParams.targetUrl,
                            transferCustomizationParams.transferCustomerAssets,
                            apiKey,
                            transferCustomizationParams.useSmartTextTransfer,
                            effectiveUseAlpha ? 'v0' : 'v3',
                            transferCustomizationParams.fixLogoContrast,
                            transferCustomizationParams.enableMulticolorLogoContrastFix,
                        ) ?? '',
                    backgroundColor: 'FFFFFF',
                })
                .build();
            const row = PreviewRow(resultCard);
            setResultRow(row);
        } else {
            setResultRow(PreviewRow());
        }
    }, [
        transferCustomizationParams.sourceUrl,
        transferCustomizationParams.targetUrl,
        transferCustomizationParams.transferCustomerAssets,
        transferCustomizationParams.useSmartTextTransfer,
        apiKey,
        effectiveUseAlpha,
        transferCustomizationParams.fixLogoContrast,
        transferCustomizationParams.enableMulticolorLogoContrastFix,
    ]);

    useEffect(() => {
        const row = PreviewRow();

        if (transferCustomizationParams.sourceUrl) {
            if (!transferCustomizationParams.useJsonSource) {
                const sourceCard = CardBuilder.ofType('documentUrl')
                    .withTitle('Source Document')
                    .withData({ documentUrl: transferCustomizationParams.sourceUrl })
                    .build();
                row.push(sourceCard);
            }
        }

        if (transferCustomizationParams.targetUrl) {
            const targetCard = CardBuilder.ofType('documentUrl')
                .withTitle('Target Document')
                .withData({ documentUrl: getUrl(transferCustomizationParams.targetUrl) ?? '' })
                .build();
            row.push(targetCard);
        }

        setOriginRow(row);
    }, [
        transferCustomizationParams.sourceUrl,
        transferCustomizationParams.targetUrl,
        transferCustomizationParams.useJsonSource,
    ]);

    return (
        <Page>
            <Page.Title>Transfer Customization</Page.Title>

            <Page.Controls>
                {SHOW_ALPHA_TOGGLE && (
                    <Control>
                        <div style={{ alignItems: 'center', display: 'flex' }}>
                            <Toggle on={useAlpha} onClick={() => setUseAlpha(!useAlpha)} size="sm" />
                            <span style={{ marginLeft: '10px' }}>Use Alpha</span>
                        </div>
                    </Control>
                )}

                <TransferCustomizationControls
                    sourceUrl={transferCustomizationParams.sourceUrl}
                    targetUrl={transferCustomizationParams.targetUrl}
                    transferCustomerAssets={transferCustomizationParams.transferCustomerAssets}
                    useSmartTextTransfer={transferCustomizationParams.useSmartTextTransfer}
                    setSourceUrl={transferCustomizationParams.setSourceUrl}
                    setTargetUrl={transferCustomizationParams.setTargetUrl}
                    setTransferCustomerAssets={transferCustomizationParams.setTransferCustomerAssets}
                    setUseSmartTextTransfer={transferCustomizationParams.setUseSmartTextTransfer}
                />

                <Control>
                    <div style={{ alignItems: 'center', display: 'flex' }}>
                        <Toggle
                            on={transferCustomizationParams.fixLogoContrast}
                            onClick={() =>
                                transferCustomizationParams.setFixLogoContrast(
                                    !transferCustomizationParams.fixLogoContrast,
                                )
                            }
                            size="sm"
                        />
                        <span style={{ marginLeft: '10px' }}>Fix Logo Contrast</span>
                    </div>
                </Control>

                <Control>
                    <div style={{ alignItems: 'center', display: 'flex' }}>
                        <Toggle
                            on={transferCustomizationParams.enableMulticolorLogoContrastFix}
                            onClick={() =>
                                transferCustomizationParams.setEnableMulticolorLogoContrastFix(
                                    !transferCustomizationParams.enableMulticolorLogoContrastFix,
                                )
                            }
                            size="sm"
                        />
                        <span style={{ marginLeft: '10px' }}>Enable Multicolor Logo Contrast Fix</span>
                    </div>
                </Control>
            </Page.Controls>

            <Page.Content>
                <PreviewSection
                    introduction={intro}
                    showIntro={!transferCustomizationParams.sourceUrl && !transferCustomizationParams.targetUrl}
                    rows={PreviewRows(originRow, resultRow)}
                />
            </Page.Content>
        </Page>
    );
};
